// ============================================================================
// THE LEARNING EXPORT — the whole Learning domain as one page of markdown, for an AI that is
// not inside LifeOS.
//
// WHY IT EXISTS. The owner wants to talk about his plans, his pace and his test scores with a
// model he is already paying for (a Claude Code chat on his Max subscription, or any chat he can
// paste into) rather than spending the runtime API budget (CLAUDE.md → AI cost policy). So the
// data has to leave the app in one piece, readable by a human and by a machine, with nothing
// summarised away — the machine-readability rule, taken all the way out of the building.
//
// WHY IT IS PURE, AND WHY IT LIVES HERE. It takes derived data in and returns a string; it reads
// no clock, no network and no database (`generatedDate` is an argument for the same reason
// `today` is one in apps/worker/src/lib/learning.ts). That is what lets the Worker route and
// apps/web's demo adapter produce the SAME page from two very different sources — the one thing
// this file is for. Two formatters would be two exports, and the coach chat would be reading a
// different document depending on which mode the owner happened to be in.
//
// WHAT IT DOES NOT DO: it computes NOTHING. Every number below — percent, the pace phrase, the
// current unit, the per-unit counts, the streak — arrives already derived by the contract
// (`detailOf` / `streakOf` on the Worker, their demo twins in the browser). A formatter that did
// its own arithmetic would be a second answer to a question the API already answers.
//
// ONE HONESTY NOTE THAT SHAPES THE OUTPUT: a session's note belongs to a DAY, not to an item —
// that is how `learning_sessions` is read back (`sessionsOf` groups by date). So a day's note is
// printed under every item finished that day, LABELLED with its date, rather than being silently
// attributed to one item. Hidden is not the same as missing, and neither is guessed.
// ============================================================================

import type { ISODate, LearningResource, LearningStreak } from './types.js'

// ── What the formatter needs, and nothing more ──────────────────────────────
//
// These are STRUCTURAL minimums, not new entity types: the Worker's `UnitItem`/`Unit`/
// `TrackDetail` (apps/worker/src/lib/learning.ts) and the browser's `LearningUnitItem`/
// `LearningUnit`/`LearningTrackDetail` (apps/web/src/lib/data/types.ts) both already satisfy
// them, so neither side converts anything on the way in. When those two shapes move down into
// @app/shared, these collapse into them.

export interface ExportItem {
  title: string
  plannedDate: ISODate
  done: boolean
  completedDate: ISODate | null
  minutes: number | null
  resource: LearningResource | null
  minutesEstimate: number | null
  instructions: string
}

export interface ExportUnit {
  name: string
  number: number
  itemsTotal: number
  itemsDone: number
  items: ExportItem[]
}

export interface ExportSessionDay {
  date: ISODate
  minutes: number
  note: string | null
}

export interface ExportTrackDetail {
  track: {
    /** The key a revision is matched on. The page prints it because the reader has to send it. */
    slug: string
    name: string
    subtitle: string
    currentUnit: string
    itemsTotal: number
    itemsDone: number
    planStartDate: ISODate
    planEndDate: ISODate
    planVersion: string
    planSource: string
    importedAt: string
  }
  percent: number
  pace: { phrase: string }
  units: ExportUnit[]
  sessions: ExportSessionDay[]
}

export interface LearningExportInput {
  /** The day the export was taken, in the owner's timezone. Never read from a clock in here. */
  generatedDate: ISODate
  /** Oldest plan first, the order the tracks endpoint answers in. */
  tracks: ExportTrackDetail[]
  streak: LearningStreak
}

// ── The page ────────────────────────────────────────────────────────────────

const INDENT = '      '

/** A timestamp's calendar day. `imported_at` is a timestamptz; the export states a date. */
function dayOf(timestamp: string): string {
  return typeof timestamp === 'string' ? timestamp.slice(0, 10) : timestamp
}

function plural(n: number, word: string): string {
  return `${n} ${n === 1 ? word : `${word}s`}`
}

/**
 * One item's line, plus its indented detail lines. `dayNote` is the note logged on the day this
 * item was finished, or null — see the honesty note in this file's header.
 */
function itemLines(item: ExportItem, dayNote: string | null): string[] {
  const head = [
    `- [${item.done ? 'x' : ' '}] ${item.title}`,
    `planned ${item.plannedDate}`,
  ]
  if (item.done) {
    // `completedDate` is the day the work HAPPENED, which is not the day the plan wanted it.
    head.push(item.completedDate ? `done ${item.completedDate}` : 'done (day not recorded)')
    // Minutes are a measurement. An item checked off without a timer says so rather than
    // carrying a zero that nobody measured.
    head.push(item.minutes === null ? 'no minutes logged' : `${item.minutes} min logged`)
  } else {
    head.push('not done')
  }

  const lines = [head.join(' · ')]
  const facts: string[] = []
  if (item.minutesEstimate !== null) facts.push(`plan estimate: ${item.minutesEstimate} min`)
  if (item.resource) facts.push(`resource: ${item.resource.label} (${item.resource.kind}) — ${item.resource.url}`)
  for (const fact of facts) lines.push(`${INDENT}${fact}`)
  // The plan's own words, verbatim, never rewritten.
  if (item.instructions) lines.push(`${INDENT}instructions: ${item.instructions}`)
  if (item.done && dayNote) lines.push(`${INDENT}note logged ${item.completedDate}: ${dayNote}`)
  return lines
}

function trackSection(detail: ExportTrackDetail): string[] {
  const t = detail.track
  const noteByDate = new Map<string, string>()
  for (const day of detail.sessions) if (day.note) noteByDate.set(day.date, day.note)

  const lines: string[] = []
  lines.push(`## ${t.name}${t.subtitle ? ` — ${t.subtitle}` : ''}`)
  lines.push('')
  lines.push(
    `slug: ${t.slug} · plan version: ${t.planVersion} · plan source: ${t.planSource} · imported ${dayOf(t.importedAt)}`,
  )
  lines.push(
    `progress: ${t.itemsDone} of ${t.itemsTotal} items done (${detail.percent}%) · ${detail.pace.phrase} · current unit: ${t.currentUnit || '(none — the plan is finished)'}`,
  )
  lines.push(`plan runs ${t.planStartDate} → ${t.planEndDate}`)
  lines.push('')

  for (const unit of detail.units) {
    lines.push(`### ${unit.name}  (unit ${unit.number} · ${unit.itemsDone} of ${unit.itemsTotal} done)`)
    if (unit.items.length === 0) lines.push('(no items in this unit)')
    for (const item of unit.items) {
      lines.push(...itemLines(item, item.completedDate ? (noteByDate.get(item.completedDate) ?? null) : null))
    }
    lines.push('')
  }
  return lines
}

function streakSection(streak: LearningStreak): string[] {
  return [
    '## Streak — across every track',
    '',
    `current: ${plural(streak.current, 'day')} · longest: ${plural(streak.longest, 'day')} · ` +
      `last day with activity: ${streak.lastDate ?? 'never'} · ` +
      `${plural(streak.days.length, 'day')} with activity in the log`,
    '',
  ]
}

/**
 * The instructions the reading AI needs, and the sentence that keeps it honest.
 *
 * It is in the document rather than in a prompt because the document is the only thing that
 * reliably travels — pasted into a chat, piped into a coach session, read a month later.
 */
function howToProposeChanges(): string[] {
  return [
    '## How to propose changes',
    '',
    'A revision is JSON in the v2 import format (docs/LEARNING-PLAN-FORMAT.md is the full',
    'contract). The essentials:',
    '',
    '- One JSON object per track, in its own fenced code block. Keep the SAME `slug` — that is',
    '  what updates the track in place instead of creating a second one.',
    '- Bump `planVersion` (`v2` → `v3`), and set `planSource` to who wrote the revision.',
    '- `units[]` and the flattened `items[]` match by POSITION, not by title. Changing an item at',
    '  a position is free; INSERTING one in the middle shifts every later completion by one.',
    '  Append at the end unless the owner accepts the shift.',
    '- Every field is REPLACED on import: leave `resource`, `minutesEstimate` or `instructions`',
    '  off an item and they are cleared. Give every item a `resource` (label + http(s) url +',
    '  kind), a `minutesEstimate` (5–240) and `instructions`.',
    '- A shorter revision deletes the surplus tail, and the sessions logged against those items',
    '  go with them. The import response reports `sessionsKept` — that is the number that proves',
    '  history survived.',
    '',
    'YOU CANNOT CHANGE ANYTHING FROM HERE. This file is a read-only snapshot. The owner imports a',
    'revision himself (`POST /api/learning/tracks`, or `npx tsx scripts/learning-export.mts',
    '--import <file.json>`). Until he says the import ran and reports back, nothing you proposed',
    'has happened — do not say a plan was updated, adjusted or rescheduled before then.',
    '',
  ]
}

/**
 * The whole export. Deterministic: same input, same bytes, every time.
 */
export function formatLearningExport(input: LearningExportInput): string {
  const lines: string[] = []
  lines.push(
    `# LifeOS learning export · generated ${input.generatedDate} · ${plural(input.tracks.length, 'track')}`,
  )
  lines.push('')
  lines.push(
    'Everything below is derived from two facts: the plan that was imported, and the sessions ' +
      'actually logged.',
  )
  lines.push(
    'Nothing here is stored progress — an item is done because a session points at it. Session ' +
      'notes are logged per DAY, so a day’s note appears under every item finished that day, ' +
      'labelled with its date.',
  )
  lines.push('')

  if (input.tracks.length === 0) {
    lines.push('No curriculum has been imported yet, so there is nothing to pace against. That is a')
    lines.push('real answer, not a failure — plans are imported into LifeOS, never authored inside it.')
    lines.push('')
  }

  for (const detail of input.tracks) lines.push(...trackSection(detail))
  lines.push(...streakSection(input.streak))
  lines.push(...howToProposeChanges())

  // One trailing newline, the way a file ends — and no blank-line run longer than one, so the
  // page stays the same shape whether a track has units left or not.
  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`
}
